import { goalAnalysisGuard } from './goal-analysis/goal-analysis.guard'
import { productGuard } from './auth/guards/product.guard'
import { Routes } from '@angular/router';
import { completeAuthGuard } from './auth/guards/complete-auth.guard';
import { scoutingGuard } from './auth/guards/scouting.guard'
import { singleCompetitionGuard, singleTeamGuard } from './auth/guards/single-landing.guard';
import { guestGuard } from './auth/guards/guest.guard';
import { roleGuard } from './auth/guards/role.guard';
import { twoFactorGuard } from './auth/guards/two-factor.guard';
import { mandatory2faSetupGuard } from './auth/guards/mandatory-2fa-setup.guard';
import { playerRedirectGuard } from './auth/guards/player-redirect.guard';
import { playerProfileGuard } from './auth/guards/player-profile.guard';
import { coachRedirectGuard } from './auth/guards/coach-redirect.guard';
import { homeRedirectGuard } from './auth/guards/home-redirect.guard';
import { academyLandingGuard } from './auth/guards/academy-landing.guard';
import { coachTeamGuard } from './auth/guards/coach-team.guard';
import { UserRole } from './shared/interfaces/user.interface';
import { competitionResolver } from './competition/competition.resolver';
import { teamResolver } from './teams/team.resolver';
import { matchResolver } from './match/match.resolver';
import { matchVideoGuard } from './shared/services/match-video-access.service';
import { uploadAreaGuard } from './upload-area/upload-area-access.service';

export const routes: Routes = [
  // Default redirect
  {
    path: '',
    redirectTo: '/home',
    pathMatch: 'full',
  },

  // Public pages carry `data: { public: true }` (Release 5, 31/10/2026, PUBLIC-02): the app shell
  // reads it and leaves out the platform bar, breadcrumbs and prompt cards, even for someone signed
  // in -- each of these pages has its own header. 'signed-out' (the live pages): public for
  // visitors only; a signed-in member keeps the bar, so there is a way back (CC, 10/10/2026).
  // A new public page needs only the flag; the shell no longer keeps a list of addresses.

  // Registration by invitation (public, no layout). "verify" before ":token" so it is not
  // taken for a token.
  {
    path: 'join/verify',
    title: 'Confirm your email',
    data: { public: true },
    loadComponent: () => import('./join/join-verify.component').then((m) => m.JoinVerifyComponent),
  },
  {
    path: 'join/:token',
    title: 'Join Science of Sports',
    data: { public: true },
    loadComponent: () => import('./join/join-page.component').then((m) => m.JoinPageComponent),
  },

  // Public auth routes (no layout)
  {
    path: 'auth/login',
    title: 'Sign in',
    loadComponent: () =>
      import('./auth/components/login/login.component').then(
        (m) => m.LoginComponent,
      ),
    canActivate: [guestGuard],
  },
  {
    path: 'auth/forgot-password',
    title: 'Forgot password',
    loadComponent: () =>
      import('./auth/components/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent,
      ),
    canActivate: [guestGuard],
  },
  {
    path: 'auth/reset-password',
    title: 'Reset password',
    loadComponent: () =>
      import('./auth/components/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
    canActivate: [guestGuard],
  },
  {
    // The welcome email's "Set up your account" link. No guard: the token is the credential,
    // and someone already signed in on a shared computer must still be able to open it.
    path: 'welcome/:token',
    title: 'Set up your account',
    data: { public: true },
    loadComponent: () =>
      import('./auth/components/welcome-setup/welcome-setup.component').then(
        (m) => m.WelcomeSetupComponent,
      ),
  },
  {
    path: 'auth/2fa-verify',
    title: 'Verify sign-in',
    loadComponent: () =>
      import('./auth/components/two-factor-verification/two-factor-verification.component').then(
        (m) => m.TwoFactorVerificationComponent,
      ),
    canActivate: [twoFactorGuard],
  },
  {
    path: 'auth/2fa-setup-mandatory',
    title: 'Set up two-factor',
    loadComponent: () =>
      import('./auth/components/mandatory-2fa-setup/mandatory-2fa-setup.component').then(
        (m) => m.Mandatory2faSetupComponent,
      ),
    canActivate: [mandatory2faSetupGuard],
  },

  // Open match link (CC, 28/09/2026): someone with an account shares a match; the viewer needs no
  // account. Outside every guard and the main layout on purpose. The token is the credential and
  // the server re-checks it on every request; only the match and its video are shown.
  {
    path: 'watch/:token',
    title: 'Watch match',
    data: { public: true },
    loadComponent: () => import('./watch/watch.component').then((m) => m.WatchComponent),
  },
  // The same page with the match's name in the address (CC, 30/09/2026) -- a label for people, the
  // token alone opens it.
  {
    path: 'watch/:slug/:token',
    title: 'Watch match',
    data: { public: true },
    loadComponent: () => import('./watch/watch.component').then((m) => m.WatchComponent),
  },

  // Upload area link (Andreas, 28/10/2026): the files an admin sent a client, WeTransfer-style. Public
  // like the watch page -- no account, no platform chrome; the token is the credential and the server
  // re-checks it on every request.
  {
    path: 'files/:token',
    title: 'Shared files',
    data: { public: true },
    loadComponent: () => import('./files/files.component').then((m) => m.FilesComponent),
  },

  // Free live matches (CC, 01/10/2026): public like the watch page -- no account, no platform chrome.
  {
    path: 'live',
    title: 'Live matches',
    data: { public: 'signed-out' },
    loadComponent: () => import('./live/live-list/live-list.component').then((m) => m.LiveListComponent),
  },
  {
    path: 'live/:fixtureId',
    title: 'Live',
    data: { public: 'signed-out' },
    loadComponent: () => import('./live/live-page/live-page.component').then((m) => m.LivePageComponent),
  },
  {
    // The named form a shared live link lands on: /live/{home-vs-away}/{fixture}, as /watch/{name}/{token}.
    path: 'live/:slug/:fixtureId',
    title: 'Live',
    data: { public: 'signed-out' },
    loadComponent: () => import('./live/live-page/live-page.component').then((m) => m.LivePageComponent),
  },

  // Customer notifications (CC, 13/10/2026): the links in "your match is ready" emails. Public:
  // each carries its own token, checked by the API. /n/:token records the click and opens the
  // match (sign-in follows through the match page's own guard).
  {
    path: 'n/:token',
    title: 'Opening your match',
    data: { public: true },
    loadComponent: () => import('./notify/notify-open.component').then((m) => m.NotifyOpenComponent),
  },
  {
    path: 'unsubscribe',
    title: 'Match emails',
    data: { public: true },
    loadComponent: () => import('./notify/unsubscribe.component').then((m) => m.UnsubscribeComponent),
  },
  {
    // An offer under a match email (Phase 2, G1): the product it showed, Pay or I'm interested.
    path: 'offer/:token/:product',
    title: 'SCIOS offer',
    data: { public: true },
    loadComponent: () => import('./notify/offer.component').then((m) => m.OfferComponent),
  },
  {
    path: 'unlock/:token',
    title: 'Unlock your match',
    data: { public: true },
    loadComponent: () => import('./notify/unlock.component').then((m) => m.UnlockComponent),
  },

  // Protected routes with main layout
  {
    path: '',
    canActivate: [completeAuthGuard],
    // Platform / Library (CC, 08/10/2026): a page of a product the account lacks redirects to
    // the one it has (auth/guards/product.guard.ts).
    canActivateChild: [productGuard],
    children: [
      {
        path: 'home',
        title: 'Home',
        loadComponent: () =>
          import('./home/home.component').then((m) => m.HomeComponent),
        // A director with one competition goes straight to it (CC, 10/10/2026); an academy
        // director to their Academy Overview (CC, 18/10/2026).
        canActivate: [playerRedirectGuard, coachRedirectGuard, homeRedirectGuard, academyLandingGuard, singleCompetitionGuard],
      },
      {
        // The Competitions page under its own address, without the academy redirect, so an
        // academy director can still open the full list (the launcher's Competitions tile).
        path: 'competitions',
        title: 'Competitions',
        loadComponent: () =>
          import('./home/home.component').then((m) => m.HomeComponent),
        canActivate: [roleGuard, singleCompetitionGuard, scoutingGuard],
        // Coaches too (access slice 1, CC 20/10/2026): the list holds only competitions granted to them.
        // Scouts too (My competitions, Andreas 27/10/2026): the competitions on their account.
        data: { roles: [UserRole.SU, UserRole.ADMIN, UserRole.DIRECTOR, UserRole.COACH, UserRole.SCOUT], allDirectors: true },
      },
      {
        // Academy Overview (CC, 18/10/2026): every youth team of the director's club on one page.
        // Admins open a club's with ?club=<id>.
        path: 'academy',
        title: 'Academy',
        loadComponent: () =>
          import('./academy/academy-overview.component').then((m) => m.AcademyOverviewComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN, UserRole.DIRECTOR] },
      },
      {
        path: 'countries',
        title: 'Countries',
        loadComponent: () =>
          import('./countries/countries.component').then(
            (m) => m.CountriesComponent,
          ),
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN] },
      },
      {
        path: 'countries/:code',
        title: 'Country',
        loadComponent: () =>
          import('./countries/country-detail/country-detail.component').then(
            (m) => m.CountryDetailComponent,
          ),
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN] },
      },
      {
        // Self-scoped by user_teams, so the list is never anyone else's. The role list is
        // here because every card opens /teams/:teamId, which admits the same four roles:
        // without it a player is shown a grid where every card denies on click.
        path: 'my-teams',
        title: 'My teams',
        loadComponent: () =>
          import('./my-teams/my-teams.component').then((m) => m.MyTeamsComponent),
        // A coach with one team goes straight to it (CC, 10/10/2026).
        canActivate: [roleGuard, singleTeamGuard],
        data: {
          roles: [
            UserRole.SU,
            UserRole.ADMIN,
            UserRole.DIRECTOR,
            UserRole.COACH,
          ],
        },
      },
      // The Video Library (CC, 07/10/2026): every match the person may watch, download and share.
      // The same four roles as the API (/v1/library) and the launcher tile; the server applies the
      // match page's access rule to every match.
      {
        path: 'library',
        title: 'Library',
        loadComponent: () =>
          import('./library/library.component').then((m) => m.LibraryComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN, UserRole.DIRECTOR, UserRole.COACH, UserRole.SCOUT] },
      },
      {
        path: 'library/:fixtureId',
        title: 'Library',
        loadComponent: () =>
          import('./library/library-player.component').then((m) => m.LibraryPlayerComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN, UserRole.DIRECTOR, UserRole.COACH, UserRole.SCOUT] },
      },
      // Scout (CC, 17/10/2026, scout Phase 1): Home, Find players and My lists. Directors may
      // open them too; what anyone finds is the server's access check (a scout's competition
      // lines, a director's competitions).
      {
        path: 'scout',
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN, UserRole.DIRECTOR, UserRole.SCOUT] },
        // A director only with the scouting option; an agent only on My players (27/10/2026).
        canActivateChild: [scoutingGuard],
        children: [
          {
            path: '',
            title: 'Scout',
            data: { agents: true },
            loadComponent: () =>
              import('./scout/scout-home/scout-home.component').then((m) => m.ScoutHomeComponent),
          },
          {
            path: 'find',
            title: 'Find players',
            loadComponent: () =>
              import('./scout/scout-find/scout-find.component').then((m) => m.ScoutFindComponent),
          },
          {
            path: 'lists',
            title: 'My lists',
            loadComponent: () =>
              import('./scout/scout-lists/scout-lists.component').then((m) => m.ScoutListsComponent),
          },
        ],
      },
      // Compare (CC, 14/10/2026): every role with screens may open it; what each one can pick
      // and see is decided by the server's access check, entity by entity and clip by clip.
      {
        path: 'compare',
        title: 'Compare',
        loadComponent: () =>
          import('./compare/compare.component').then((m) => m.CompareComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN, UserRole.DIRECTOR, UserRole.COACH, UserRole.PLAYER, UserRole.SCOUT] },
      },
      // Playlists are per user and open to every signed-in role (the settings/profile
      // precedent): every clip a playlist holds was access-checked when it was added, and a
      // shared link shows a viewer only the clips from matches they may watch.
      // 'playlists/shared/:token' is declared before 'playlists/:id' so a bare
      // /playlists/shared never opens a detail page with id = 'shared'.
      {
        path: 'playlists/shared/:token',
        title: 'Shared playlist',
        loadComponent: () =>
          import('./playlists/playlist-detail/playlist-detail.component').then((m) => m.PlaylistDetailComponent),
        data: { shared: true },
      },
      {
        // Video inbox + Add match video. Not under /admin: the permission (MANAGE_MATCH_VIDEOS) can
        // be granted to a director without making them an admin.
        path: 'match-videos',
        title: 'Video inbox',
        loadComponent: () =>
          import('./admin/components/video-inbox/video-inbox.component').then((m) => m.VideoInboxComponent),
        canActivate: [matchVideoGuard],
      },
      {
        // Upload area (Andreas, 28/10/2026): send clients videos, presentations and analysis by link.
        // Not under /admin: the permission (USE_UPLOAD_AREA) is meant to be sold to directors and
        // coaches later. Admins only for now.
        path: 'upload-area',
        title: 'Upload area',
        loadComponent: () => import('./upload-area/upload-area.component').then((m) => m.UploadAreaComponent),
        canActivate: [uploadAreaGuard],
      },
      {
        // The operators' list of live streams (CC, 03/10/2026): same permission as the Video inbox.
        path: 'live-streams',
        title: 'Live streams',
        loadComponent: () =>
          import('./live/live-streams/live-streams.component').then((m) => m.LiveStreamsComponent),
        canActivate: [matchVideoGuard],
      },
      {
        // Match control for a live match: the same people who manage match videos (CC, 01/10/2026).
        path: 'live-control/:fixtureId',
        title: 'Match control',
        loadComponent: () =>
          import('./live/live-control/live-control.component').then((m) => m.LiveControlComponent),
        canActivate: [matchVideoGuard],
      },
      {
        // Release B2: a playlist someone shared with the signed-in user. Declared before
        // 'playlists/:id' for the same reason as 'playlists/shared/:token'.
        path: 'playlists/with-me/:id',
        title: 'Shared with me',
        loadComponent: () =>
          import('./playlists/playlist-detail/playlist-detail.component').then((m) => m.PlaylistDetailComponent),
        data: { recipient: true },
      },
      {
        path: 'playlists',
        title: 'Playlists',
        loadComponent: () =>
          import('./playlists/playlists.component').then((m) => m.PlaylistsComponent),
      },
      {
        path: 'playlists/:id',
        title: 'Playlist',
        loadComponent: () =>
          import('./playlists/playlist-detail/playlist-detail.component').then((m) => m.PlaylistDetailComponent),
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./user/user.routes').then((m) => m.userRoutes),
      },
      {
        path: 'admin',
        loadChildren: () =>
          import('./admin/admin.routes').then((m) => m.adminRoutes),
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN] },
      },
      {
        // Nothing links here any more. The countries browser was its only entry point and
        // now lists the national competitions themselves; the page this pointed at listed
        // every participant of an age group across all countries, so a tile labelled
        // "Cyprus U16" opened a list containing Wales and Romania. Redirected rather than
        // deleted, so an existing bookmark lands somewhere honest.
        path: 'national-teams/:ageGroup',
        redirectTo: '/countries',
      },
      {
        path: 'competition/:competitionId',
        canActivate: [roleGuard],
        data: {
          // Coaches: competitions granted to them (access slice 1, CC 20/10/2026); the server decides which.
          roles: [
            UserRole.SU,
            UserRole.ADMIN,
            UserRole.DIRECTOR,
            UserRole.SCOUT,
            UserRole.COACH,
            // Referees (CC, 27/10/2026): the competitions they work in; the server decides which.
            UserRole.REFEREE,
          ],
        },
        resolve: { competition: competitionResolver },
        loadComponent: () =>
          import('./competition/competition-layout/competition-layout.component').then(
            (m) => m.CompetitionLayoutComponent,
          ),
        children: [
          {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full',
          },
          {
            path: 'overview',
            title: 'Overview',
            loadComponent: () =>
              import('./competition/overview/overview.component').then(
                (m) => m.CompetitionOverviewComponent,
              ),
          },
          {
            path: 'matches',
            title: 'Matches',
            loadComponent: () =>
              import('./competition/matches/matches.component').then(
                (m) => m.CompetitionMatchesComponent,
              ),
          },
          {
            path: 'teams',
            title: 'Teams',
            loadComponent: () =>
              import('./competition/teams/teams.component').then(
                (m) => m.CompetitionTeamsComponent,
              ),
          },
          {
            path: 'rankings',
            title: 'Rankings',
            loadComponent: () =>
              import('./competition/rankings/rankings.component').then(
                (m) => m.CompetitionRankingsComponent,
              ),
          },
          // The player and team rankings tabs were merged into one.
          { path: 'rankings/player', redirectTo: 'rankings', pathMatch: 'full' },
          { path: 'rankings/team', redirectTo: 'rankings', pathMatch: 'full' },
          {
            path: 'stats',
            title: 'Stats',
            loadComponent: () =>
              import('./competition/stats/stats.component').then(
                (m) => m.CompetitionStatsComponent,
              ),
          },
          // Goal Analysis (Andreas, 30/10/2026): admins and the Goal Analysis add-on.
          {
            path: 'goal-analysis',
            title: 'Goal Analysis',
            canActivate: [goalAnalysisGuard],
            loadComponent: () =>
              import('./goal-analysis/goal-analysis-page.component').then(
                (m) => m.CompetitionGoalAnalysisComponent,
              ),
          },
        ],
      },
      {
        path: 'teams/:teamId',
        canActivate: [roleGuard, coachTeamGuard],
        data: {
          roles: [
            UserRole.SU,
            UserRole.ADMIN,
            UserRole.DIRECTOR,
            UserRole.COACH,
            UserRole.SCOUT,
            // Referees (CC, 27/10/2026): the competitions they work in; the server decides which.
            UserRole.REFEREE,
          ],
        },
        resolve: { team: teamResolver },
        loadComponent: () =>
          import('./teams/team-layout/team-layout.component').then(
            (m) => m.TeamLayoutComponent,
          ),
        children: [
          {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full',
          },
          {
            path: 'overview',
            title: 'Overview',
            loadComponent: () =>
              import('./teams/overview/team-overview.component').then(
                (m) => m.TeamOverviewComponent,
              ),
          },
          {
            path: 'matches',
            title: 'Matches',
            loadComponent: () =>
              import('./teams/matches/matches.component').then(
                (m) => m.TeamMatchesComponent,
              ),
          },
          {
            path: 'rankings',
            title: 'Rankings',
            loadComponent: () =>
              import('./teams/rankings/rankings.component').then(
                (m) => m.TeamRankingsComponent,
              ),
          },
          {
            path: 'stats',
            title: 'Stats',
            loadComponent: () =>
              import('./teams/stats/stats.component').then(
                (m) => m.TeamStatsComponent,
              ),
          },
          {
            path: 'players',
            title: 'Players',
            loadComponent: () =>
              import('./teams/players/players.component').then(
                (m) => m.TeamPlayersComponent,
              ),
          },
          // Team Videos (CC, 17/10/2026): the season's goals, shots and set pieces, for and
          // against, in one place. Same guards as the other team tabs.
          {
            path: 'videos',
            title: 'Videos',
            loadComponent: () =>
              import('./teams/videos/team-videos.component').then(
                (m) => m.TeamVideosComponent,
              ),
          },
          // Goal Analysis (Andreas, 30/10/2026): the team's goals scored and conceded.
          {
            path: 'goal-analysis',
            title: 'Goal Analysis',
            canActivate: [goalAnalysisGuard],
            loadComponent: () =>
              import('./goal-analysis/goal-analysis-page.component').then(
                (m) => m.TeamGoalAnalysisComponent,
              ),
          },
        ],
      },
      // Referee profile (CC, 18/10/2026): admins only until the referee packages open.
      {
        path: 'referees',
        title: 'Referees',
        loadComponent: () =>
          import('./referees/referee-list/referee-list.component').then((m) => m.RefereeListComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN] },
      },
      // Referee & observer accounts (CC, 20/10/2026): a Referee / observer login lands on /referee
      // (their own page), observes from /observer, and reads or writes reports at
      // /observer-reports/:fixtureId; the department (admins) works from /observer-reports.
      {
        path: 'referee',
        title: 'My referee page',
        loadComponent: () =>
          import('./referees/referee-landing/referee-landing.component').then((m) => m.RefereeLandingComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.REFEREE] },
      },
      {
        path: 'observer',
        title: 'My appointments',
        loadComponent: () =>
          import('./referees/observer-appointments/observer-appointments.component').then((m) => m.ObserverAppointmentsComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.REFEREE] },
      },
      {
        path: 'observer-reports',
        title: 'Observer reports',
        loadComponent: () =>
          import('./referees/department-reports/department-reports.component').then((m) => m.DepartmentReportsComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN] },
      },
      {
        path: 'observer-reports/:fixtureId',
        title: 'Observer report',
        loadComponent: () =>
          import('./referees/observer-report/observer-report.component').then((m) => m.ObserverReportComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN, UserRole.REFEREE] },
      },
      {
        path: 'referees/:id',
        loadComponent: () =>
          import('./referees/referee-layout/referee-layout.component').then((m) => m.RefereeLayoutComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.SU, UserRole.ADMIN, UserRole.REFEREE] },
        children: [
          { path: '', redirectTo: 'overview', pathMatch: 'full' },
          {
            // Self-review and Reports in one tab (CC, 27/10/2026): his view of every decision and,
            // once released, the observer's beside it. The old addresses land on it.
            path: 'review',
            title: 'Referee · Review',
            loadComponent: () =>
              import('./referees/referee-review/referee-review.component').then((m) => m.RefereeReviewComponent),
          },
          { path: 'self-review', redirectTo: 'review', pathMatch: 'full' },
          { path: 'reports', redirectTo: 'review', pathMatch: 'full' },
          {
            // A match's Refereeing view inside the referee's own pages: a referee never opens
            // the club match page.
            path: 'match/:fixtureId',
            title: 'Referee · Match',
            loadComponent: () =>
              import('./match/refereeing/refereeing.component').then((m) => m.MatchRefereeingComponent),
          },
          {
            path: 'overview',
            title: 'Referee',
            loadComponent: () =>
              import('./referees/referee-overview/referee-overview.component').then((m) => m.RefereeOverviewComponent),
          },
          {
            path: 'matches',
            title: 'Referee · Matches',
            loadComponent: () =>
              import('./referees/referee-matches/referee-matches.component').then((m) => m.RefereeMatchesComponent),
          },
          {
            // The Competition Overview built for referees, and its teams (CC, 27/10/2026).
            path: 'competition',
            title: 'Referee · Competition',
            loadComponent: () =>
              import('./referees/referee-competition/referee-competition.component').then((m) => m.RefereeCompetitionComponent),
          },
          {
            path: 'teams',
            title: 'Referee · Teams',
            loadComponent: () =>
              import('./referees/referee-teams/referee-teams.component').then((m) => m.RefereeTeamsComponent),
          },
          {
            path: 'stats',
            title: 'Referee · Stats',
            loadComponent: () =>
              import('./referees/referee-stats/referee-stats.component').then((m) => m.RefereeStatsComponent),
          },
          {
            path: 'videos',
            title: 'Referee · Videos',
            loadComponent: () =>
              import('./referees/referee-videos/referee-videos.component').then((m) => m.RefereeVideosComponent),
          },
          {
            path: 'compare',
            title: 'Referee · Compare',
            loadComponent: () =>
              import('./referees/referee-compare/referee-compare.component').then((m) => m.RefereeCompareComponent),
          },
        ],
      },
      {
        path: 'players/:id',
        loadComponent: () =>
          import('./players/player-layout/player-layout.component').then(
            (m) => m.PlayerLayoutComponent,
          ),
        canActivate: [roleGuard, playerProfileGuard],
        data: {
          roles: [
            UserRole.SU,
            UserRole.ADMIN,
            UserRole.DIRECTOR,
            UserRole.COACH,
            UserRole.PLAYER,
            UserRole.SCOUT,
            // Referees (CC, 27/10/2026): the competitions they work in; the server decides which.
            UserRole.REFEREE,
          ],
        },
        children: [
          {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full',
          },
          {
            path: 'overview',
            title: 'Overview',
            loadComponent: () =>
              import('./players/overview/player-overview.component').then(
                (m) => m.PlayerOverviewComponent,
              ),
          },
          {
            path: 'matches',
            title: 'Matches',
            loadComponent: () =>
              import('./players/matches/player-matches.component').then(
                (m) => m.PlayerMatchesComponent,
              ),
          },
          {
            path: 'match-analysis/:matchId',
            title: 'Match analysis',
            loadComponent: () =>
              import('./players/match-analysis/player-match-analysis.component').then(
                (m) => m.PlayerMatchAnalysisComponent,
              ),
          },
          {
            path: 'stats',
            title: 'Stats',
            loadComponent: () =>
              import('./players/player.component').then(
                (m) => m.PlayerComponent,
              ),
          },
          {
            path: 'performance',
            title: 'Performance',
            loadComponent: () =>
              import('./players/performance/player-performance.component').then(
                (m) => m.PlayerPerformanceComponent,
              ),
          },
          {
            path: 'videos',
            title: 'Videos',
            loadComponent: () =>
              import('./players/videos/player-videos.component').then(
                (m) => m.PlayerVideosComponent,
              ),
          },
          // Goal Analysis for a player (phase 3, 02/11/2026): admins and the Goal Analysis add-on.
          {
            path: 'goal-analysis',
            title: 'Goal Analysis',
            canActivate: [goalAnalysisGuard],
            loadComponent: () =>
              import('./goal-analysis/goal-analysis-player.component').then(
                (m) => m.PlayerGoalAnalysisComponent,
              ),
          },
        ],
      },
      {
        // "My notifications" (customer notifications Phase 2, G3).
        path: 'settings/notifications',
        title: 'My notifications',
        loadComponent: () =>
          import('./settings/notification-settings.component').then(
            (m) => m.NotificationSettingsComponent,
          ),
      },
      {
        // The old "Settings — Coming Soon" page is gone; its one real item was notifications
        // (mobile audit ROLES-17, 02/11/2026).
        path: 'settings',
        pathMatch: 'full',
        redirectTo: 'settings/notifications',
      },
      {
        path: 'matches/:matchId',
        canActivate: [roleGuard],
        data: {
          roles: [
            UserRole.SU,
            UserRole.ADMIN,
            UserRole.DIRECTOR,
            UserRole.COACH,
            UserRole.PLAYER,
            UserRole.SCOUT,
            // Referees (CC, 27/10/2026): the competitions they work in; the server decides which.
            UserRole.REFEREE,
          ],
        },
        resolve: { match: matchResolver },
        loadComponent: () =>
          import('./match/match-layout/match-layout.component').then(
            (m) => m.MatchLayoutComponent,
          ),
        children: [
          {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full',
          },
          {
            path: 'overview',
            title: 'Overview',
            loadComponent: () =>
              import('./match/overview/overview.component').then(
                (m) => m.MatchOverviewComponent,
              ),
          },
          {
            path: 'events',
            title: 'Events',
            loadComponent: () =>
              import('./match/timeline/timeline.component').then(
                (m) => m.MatchTimelineComponent,
              ),
          },
          // This tab was named "timeline".
          { path: 'timeline', redirectTo: 'events', pathMatch: 'full' },
          {
            path: 'stats',
            title: 'Stats',
            loadComponent: () =>
              import('./match/stats/stats.component').then(
                (m) => m.MatchStatsComponent,
              ),
            // Players too, in a match their team played (Phase 0.1, CC 13/10/2026).
            canActivate: [roleGuard],
            data: { roles: [UserRole.SU, UserRole.ADMIN, UserRole.DIRECTOR, UserRole.COACH, UserRole.PLAYER, UserRole.SCOUT] },
          },
          {
            path: 'videos',
            title: 'Videos',
            loadComponent: () =>
              import('./match/videos/videos.component').then(
                (m) => m.MatchVideosComponent,
              ),
          },
          {
            path: 'passing',
            title: 'Passing',
            loadComponent: () =>
              import('./match/passing/passing.component').then(
                (m) => m.MatchPassingComponent,
              ),
          },
          {
            // Referee profile step 2 (CC, 18/10/2026): admins only until the referee packages open.
            path: 'refereeing',
            title: 'Refereeing',
            loadComponent: () =>
              import('./match/refereeing/refereeing.component').then(
                (m) => m.MatchRefereeingComponent,
              ),
            canActivate: [roleGuard],
            data: { roles: [UserRole.SU, UserRole.ADMIN] },
          },
        ],
      },
    ],
  },

  // Unauthorized page (no layout)
  {
    path: 'unauthorized',
    title: 'Access denied',
    loadComponent: () =>
      import('./shared/components/unauthorized/unauthorized.component').then(
        (m) => m.UnauthorizedComponent,
      ),
  },

  // Wildcard route - must be last
  {
    path: '**',
    redirectTo: '/home',
  },
];
